import React, { useEffect } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { Row, Form, message } from "antd";
import { PaperClipOutlined, PlusOutlined } from "@ant-design/icons";
import { RequiredFormLabel } from "../RequiredFormLabel/RequiredFormLabel";
import AddUserPopertyTopBar from "../AddUserPopertyTopBar";
import OnboardingPropertyDromoUploader from "../../DromoUploader/Onboarding/OnboardingPropertyDromoUploader";
import PMSSelect from "../PMSSelect/PMSSelect";
import { Input, Select } from "../../Common/Input/index";
import { ButtonLink, ButtonPrimary } from "../../Common/Button/index";
import { useGetPMS } from "../../../hooks/pms.hooks";
import { useRemoveProperties } from "../../../hooks/Onboarding/onboarding-properties.hooks";
import { TimezoneList } from "../../../utils/common-utils";
import "./styles.css";

let minHeightPX = `${window.innerHeight - 320}px`;

const Timezones = TimezoneList.map((time) => {
  return {
    value: time,
    label: time,
  };
});

const PropertyUploader = (props) => {
  const { control, errors, isAddPropertyDisabled } = props;

  useEffect(() => {
    if (errors?.propertyData?.type === "exceed-maximum-property-count") {
      message.error(errors.propertyData?.message);
    }
  }, [errors?.propertyData]);

  const { fields, remove, append } = useFieldArray({
    control,
    name: "propertyData",
  });
  const { data: pmsData } = useGetPMS();

  const { mutate: removePropertyList } = useRemoveProperties({
    onSuccess: () => {
      message.success("Properties successfully removed");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const addProperty = (
    propertyObject = {
      property_name: "",
      property_code: "",
      brand: "",
      pms: "",
      pos: "",
      property_time_zone: "",
      property_contact: {
        first_name: "",
        last_name: "",
        email: "",
      },
    },
  ) => {
    append(propertyObject);
  };

  const removeProperty = (id, property) => {
    if (property._id) {
      removePropertyList({ propertyList: Array(property._id) });
      remove(id);
    } else {
      remove(id);
    }
  };

  const resultsHandler = (resultData) => {
    const processedData = resultData.map((property) => {
      const pms = pmsData.find((pms) => pms.pms_name === property.pms);
      const formattedProperty = {
        property_name: property.property_name,
        property_code: property.property_code,
        brand: property.brand,
        pms: pms?._id || "",
        pos: property.pos,
        property_time_zone: property.property_time_zone,
        property_contact: {
          first_name: property.property_contact_first_name,
          last_name: property.property_contact_last_name,
          email: property.property_contact_email,
        },
      };
      return formattedProperty;
    });
    addProperty(processedData);
  };

  const propertyList = fields.map((property, index) => {
    return (
      <tr key={property._id}>
        <td>
          <Form.Item
            label={
              index === 0 ? <RequiredFormLabel value="Property Name" /> : ""
            }
            validateStatus={
              errors?.propertyData?.[index]?.property_name ? "error" : ""
            }
          >
            <Controller
              control={control}
              name={`propertyData.${index}.property_name`}
              render={({ field: { onChange, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  style={{ width: "300px" }}
                  placeholder="Property Name"
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item
            label={index === 0 ? "Property Code" : ""}
            help={errors?.propertyData?.[index]?.property_code?.message}
            validateStatus={
              errors?.propertyData?.[index]?.property_code ? "error" : ""
            }
          >
            <Controller
              control={control}
              name={`propertyData.${index}.property_code`}
              render={({ field: { onChange, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  placeholder="Property Code"
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item label={index === 0 ? "Brand" : ""}>
            <Controller
              control={control}
              name={`propertyData.${index}.brand`}
              render={({ field: { onChange, value } }) => (
                <Input value={value} onChange={onChange} placeholder="Brand" />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item
            label={index === 0 ? <RequiredFormLabel value="PMS" /> : ""}
            validateStatus={errors?.propertyData?.[index]?.pms ? "error" : ""}
          >
            <Controller
              control={control}
              name={`propertyData.${index}.pms`}
              render={({ field: { onChange, value } }) => (
                <PMSSelect value={value} onChange={onChange} />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item
            label={index === 0 ? <RequiredFormLabel value="Time Zone" /> : ""}
            validateStatus={
              errors?.propertyData?.[index]?.property_time_zone ? "error" : ""
            }
          >
            <Controller
              control={control}
              name={`propertyData.${index}.property_time_zone`}
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value}
                  onChange={onChange}
                  placeholder="Time Zone"
                  options={Timezones}
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item label={index === 0 ? "POS" : ""}>
            <Controller
              control={control}
              name={`propertyData.${index}.pos`}
              render={({ field: { onChange, value } }) => (
                <Input value={value} onChange={onChange} placeholder="POS" />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item label={index === 0 ? "Property Contact First Name" : ""}>
            <Controller
              control={control}
              name={`propertyData.${index}.property_contact.first_name`}
              render={({ field: { onChange, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  placeholder="Property Contact First Name"
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item label={index === 0 ? "Property Contact Last Name" : ""}>
            <Controller
              control={control}
              name={`propertyData.${index}.property_contact.last_name`}
              render={({ field: { onChange, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  placeholder="Property Contact Last Name"
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <Form.Item
            label={index === 0 ? "Property Contact Email" : ""}
            validateStatus={
              errors?.propertyData?.[index]?.property_contact?.email
                ? "error"
                : ""
            }
          >
            <Controller
              control={control}
              name={`propertyData.${index}.property_contact.email`}
              render={({ field: { onChange, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  placeholder="Property Contact Email"
                />
              )}
            />
          </Form.Item>
        </td>
        <td>
          <ButtonLink
            onClick={() => {
              removeProperty(index, property);
            }}
            style={{ marginBottom: "15px" }}
          >
            Remove
          </ButtonLink>
        </td>
      </tr>
    );
  });

  return (
    <div
      className="onboarding-property-upload-form"
      style={{ minHeight: minHeightPX }}
    >
      <Row>
        <AddUserPopertyTopBar
          addProperty={addProperty}
          isAddPropertyDisabled={isAddPropertyDisabled}
        />
      </Row>
      <Row>
        {pmsData ? (
          <OnboardingPropertyDromoUploader
            resultsHandler={resultsHandler}
            dropboxInnerContent={
              <>
                <PaperClipOutlined /> Upload or Drop Template Files Here
              </>
            }
          />
        ) : (
          <></>
        )}
      </Row>
      <Form layout="vertical">
        {propertyList.length > 0 ? (
          <div className="property-container">
            <table>
              <tbody>{propertyList}</tbody>
            </table>
            <ButtonPrimary
              onClick={() => {
                addProperty();
              }}
            >
              <PlusOutlined /> Add Property
            </ButtonPrimary>
          </div>
        ) : null}
      </Form>
    </div>
  );
};

export default PropertyUploader;
